// PackageGenerator — HEADLESS, off-screen generation of the official package. It mounts hidden, loads the
// same render inputs as PackageViewer, renders ReportDocument off-screen (so the MVP html2canvas body can be
// captured), then calls generateOfficialPackage and reports back via onComplete. Used at office approval so
// generation starts in the BACKGROUND — no modal is opened. It does NOT touch the old raster flow, the
// PackageViewer, client acknowledgement, or WSHC. Self-unmounts (the parent drops it on onComplete).
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import ReportDocument from './ReportDocument'
import { resolveSnapshotMedia } from '../lib/reportAppendix'
import { generateOfficialPackage } from '../lib/reportPackage'
import { bizsafeReportName } from '../lib/reportFileName'

export default function PackageGenerator({ jobId, job, client, onComplete }) {
  const [snap, setSnap] = useState(null)
  const [ack, setAck] = useState(null)
  const [logos, setLogos] = useState({})
  const [media, setMedia] = useState({})
  const [templateItems, setTemplateItems] = useState(null)
  const [checklistRev, setChecklistRev] = useState(null)
  const [ready, setReady] = useState(false)   // data + inline media loaded → safe to render & capture
  const ref = useRef(null)
  const started = useRef(false)

  // 1) Load exactly what ReportDocument needs (same sources as PackageViewer; media via the shared resolver).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data: sarr } = await supabase.from('audit_report_snapshots').select('*')
          .eq('job_id', jobId).order('version', { ascending: false }).limit(1)
        const s = sarr?.[0] || null
        if (!s) { onComplete?.({ ok: false, error: 'no snapshot' }); return }
        const { data: fs } = await supabase.from('firm_settings')
          .select('firm_logo_base64, sac_logo_base64, stamp_base64, official_package_enabled').eq('id', 1).maybeSingle()
        if (fs?.official_package_enabled === false) { onComplete?.({ ok: false, error: 'disabled' }); return }
        const { data: a } = await supabase.from('report_ack_responses').select('*')
          .eq('job_id', jobId).order('submitted_at', { ascending: false }).limit(1)
        const inner = s?.snapshot || {}
        let cr = inner.checklist_revision || null
        if (!cr && inner.checklist_template_id) {
          const { data: tpl } = await supabase.from('checklist_templates').select('revision').eq('id', inner.checklist_template_id).maybeSingle()
          cr = tpl?.revision || null
        }
        let ti = null
        if (!(inner.checklist || []).length && inner.checklist_template_id) {
          const { data } = await supabase.from('checklist_template_items')
            .select('id, clause_ref, section, section_order, item_text')
            .eq('template_id', inner.checklist_template_id).is('archived_at', null).order('item_order')
          ti = data || null
        }
        const m = await resolveSnapshotMedia(inner.evidence)
        if (cancelled) return
        setSnap(s); setAck(a?.[0] || null)
        setLogos({ company: fs?.firm_logo_base64 || '', sac: fs?.sac_logo_base64 || '', stamp: fs?.stamp_base64 || '' })
        setChecklistRev(cr); setTemplateItems(ti); setMedia(m); setReady(true)
      } catch (e) { onComplete?.({ ok: false, error: String(e) }) }
    })()
    return () => { cancelled = true }
  }, [jobId])

  // 2) Once rendered + media resolved, generate exactly once (short delay lets images paint before capture).
  useEffect(() => {
    if (!ready || started.current || !ref.current) return
    started.current = true
    const t = setTimeout(async () => {
      const res = await generateOfficialPackage({
        jobId, version: snap.version, bodyElement: ref.current, snapshotEvidence: (snap.snapshot || {}).evidence,
        fileName: bizsafeReportName({ ...job, clients: client }, snap.version),
      })
      onComplete?.(res)
    }, 400)
    return () => clearTimeout(t)
  }, [ready])

  if (!ready || !snap) return null
  // Off-screen with REAL layout (html2canvas needs computed layout; a negative offset keeps it invisible to
  // the user but fully rendered for capture). Never printed, never part of the visible page.
  return (
    <div aria-hidden style={{ position: 'fixed', left: -100000, top: 0, width: 820, background: '#fff', pointerEvents: 'none' }}>
      <div ref={ref} style={{ padding: '6px 6px 18px' }}>
        <ReportDocument doc={{ client, job, snapshot: snap, ack, currentVersion: snap.version }}
          logos={logos} media={media} templateItems={templateItems} checklistRevision={checklistRev} />
      </div>
    </div>
  )
}
